import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Alert,
  CircularProgress,
  Grid,
} from '@mui/material';
import { format, parse } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { calendarService } from '../../services/calendarService';
import { businessConfigService } from '../../services/businessConfigService';

/**
 * BookMeetingDialog - Form dialog for booking new meetings or rescheduling existing ones
 */
const BookMeetingDialog = ({ 
  open, 
  onClose, 
  customerId, 
  calendarName, 
  prefilledDate,
  existingMeeting = null,
  isRescheduling = false
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    calendarName: calendarName || '',
    meetingType: '',
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    startDateTime: prefilledDate || new Date(),
    notes: '',
  });

  const [calendars, setCalendars] = useState([]);
  const [meetingTypes, setMeetingTypes] = useState([]);
  const [selectedDuration, setSelectedDuration] = useState(60);
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Pre-fill form with existing meeting data when rescheduling
  useEffect(() => {
    if (isRescheduling && existingMeeting && open && meetingTypes.length > 0) {
      setFormData({
        calendarName: existingMeeting.calendarName || calendarName || '',
        meetingType: existingMeeting.type || '',
        customerName: existingMeeting.customerName || '',
        customerEmail: existingMeeting.customerEmail || '',
        customerPhone: existingMeeting.customerPhone || '',
        startDateTime: prefilledDate || existingMeeting.start || new Date(),
        notes: existingMeeting.notes || '',
      });
    }
  }, [isRescheduling, existingMeeting, calendarName, prefilledDate, open, meetingTypes]);

  useEffect(() => {
    const fetchCalendars = async () => {
      try {
        const calendarList = await businessConfigService.getBusinessCalendars(customerId);
        setCalendars(calendarList);

        // Pre-select calendar and set meeting types
        if (calendarName && calendarList.length > 0) {
          const calendar = calendarList.find(c => c.name === calendarName);
          if (calendar?.meeting_types) {
            // Convert object to array if needed
            const typesArray = Array.isArray(calendar.meeting_types)
              ? calendar.meeting_types
              : Object.entries(calendar.meeting_types).map(([key, value]) => ({
                  key,
                  name: value.name || key,
                  duration: value.duration || 60,
                  description: value.description,
                  goal: value.goal
                }));
            setMeetingTypes(typesArray);
          }
        }
      } catch (err) {
        console.error('Error fetching calendars:', err);
      }
    };

    if (open && customerId) {
      fetchCalendars();
    }
  }, [open, customerId, calendarName]);

  useEffect(() => {
    // Update startDateTime when prefilledDate changes
    if (prefilledDate) {
      setFormData(prev => ({ ...prev, startDateTime: prefilledDate }));
    }
  }, [prefilledDate]);

  useEffect(() => {
    // Update meeting types when calendar changes
    const calendar = calendars.find(c => c.name === formData.calendarName);
    
    if (calendar?.meeting_types) {
      // Convert object to array if needed
      const typesArray = Array.isArray(calendar.meeting_types)
        ? calendar.meeting_types
        : Object.entries(calendar.meeting_types).map(([key, value]) => ({
            key,
            name: value.name || key,
            duration: value.duration || 60,
            description: value.description,
            goal: value.goal
          }));
      
      setMeetingTypes(typesArray);
      
      // Don't auto-reset meeting type when rescheduling - let the prefill effect handle it
      if (!isRescheduling && !formData.meetingType) {
      }
    } else {
      setMeetingTypes([]);
    }
  }, [formData.calendarName, calendars]);

  useEffect(() => {
    // Update duration when meeting type changes
    if (Array.isArray(meetingTypes) && meetingTypes.length > 0) {
      const selectedMeetingType = meetingTypes.find(mt => (mt.key || mt.name) === formData.meetingType);
      if (selectedMeetingType?.duration) {
        setSelectedDuration(selectedMeetingType.duration);
      }
    }
  }, [formData.meetingType, meetingTypes]);

  useEffect(() => {
    // Check availability when date/time changes
    const checkAvailability = async () => {
      if (formData.calendarName && formData.startDateTime) {
        try {
          const available = await calendarService.isSlotAvailable(
            customerId,
            formData.calendarName,
            formData.startDateTime,
            selectedDuration
          );
          setAvailability(available ? 'available' : 'occupied');
        } catch (err) {
          console.error('Error checking availability:', err);
          setAvailability(null);
        }
      }
    };

    if (open) {
      checkAvailability();
    }
  }, [formData.startDateTime, formData.calendarName, selectedDuration, customerId, open]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.calendarName) {
      setError('Please select a calendar');
      return;
    }
    if (!formData.meetingType) {
      setError('Please select a meeting type');
      return;
    }
    if (!formData.customerName.trim()) {
      setError('Customer name is required');
      return;
    }
    if (!formData.customerEmail.trim() && !formData.customerPhone.trim()) {
      setError(t('booking.provideEmailOrPhone'));
      return;
    }
    if (availability === 'occupied') {
      setError(t('booking.slotNotAvailable'));
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const startTime = formData.startDateTime;
      const endTime = new Date(startTime.getTime() + selectedDuration * 60000);

      const meetingData = {
        customer_id: customerId,
        calendar_name: formData.calendarName,
        meeting_type: formData.meetingType,
        customer_name: formData.customerName,
        customer_email: formData.customerEmail,
        customer_telephone: formData.customerPhone,
        slot_start: format(startTime, "yyyy-MM-dd'T'HH:mm:ss"),
        slot_end: format(endTime, "yyyy-MM-dd'T'HH:mm:ss"),
        notes: formData.notes,
      };

      if (isRescheduling && existingMeeting) {
        // Rescheduling existing meeting
        // Get browser timezone
        const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        await calendarService.rescheduleMeeting(
          customerId,
          formData.calendarName,
          existingMeeting.id,
          startTime,
          endTime,
          browserTimezone
        );
      } else {
        // Booking new meeting
        await calendarService.bookMeeting(meetingData);
      }

      setSuccess(true);
      
      // Reset form immediately on success
      setFormData({
        calendarName: calendarName || '',
        meetingType: '',
        customerName: '',
        customerEmail: '',
        customerPhone: '',
        startDateTime: prefilledDate || new Date(),
        notes: '',
      });
      
      setTimeout(() => {
        setSuccess(false);
        onClose(true); // Pass true to indicate successful booking/rescheduling
      }, 1500);
    } catch (err) {
      setError(err.message || 'Failed to book meeting');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        calendarName: calendarName || '',
        meetingType: '',
        customerName: '',
        customerEmail: '',
        customerPhone: '',
        startDateTime: prefilledDate || new Date(),
        notes: '',
      });
      setError(null);
      setSuccess(false);
      onClose(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isRescheduling ? t('booking.rebookMeeting') : t('booking.bookMeeting')}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t('booking.bookingSuccess')}
            </Alert>
          )}
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              select
              label={t('booking.calendar')}
              value={formData.calendarName}
              onChange={(e) => handleChange('calendarName', e.target.value)}
              disabled={loading || success || !!calendarName}
              required
              fullWidth
            >
              {calendars.map((cal) => (
                <MenuItem key={cal.name} value={cal.name}>
                  {cal.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label={t('booking.meetingType')}
              value={formData.meetingType}
              onChange={(e) => handleChange('meetingType', e.target.value)}
              disabled={!formData.calendarName || loading || success}
              required
              fullWidth
            >
              {Array.isArray(meetingTypes) && meetingTypes.map((mt) => (
                <MenuItem key={mt.key || mt.name} value={mt.key || mt.name}>
                  {(mt.description || mt.name || mt.key)} ({mt.duration} min)
                </MenuItem>
              ))}
            </TextField>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('booking.date')}
                  type="date"
                  value={formData.startDateTime ? format(formData.startDateTime, 'yyyy-MM-dd') : ''}
                  onChange={(e) => {
                    const dateStr = e.target.value;
                    const timeStr = formData.startDateTime ? format(formData.startDateTime, 'HH:mm') : '09:00';
                    const newDate = parse(`${dateStr} ${timeStr}`, 'yyyy-MM-dd HH:mm', new Date());
                    handleChange('startDateTime', newDate);
                  }}
                  disabled={loading || success}
                  required
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  error={availability === 'occupied'}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('booking.time')}
                  type="time"
                  value={formData.startDateTime ? format(formData.startDateTime, 'HH:mm') : ''}
                  onChange={(e) => {
                    const timeStr = e.target.value;
                    const dateStr = formData.startDateTime ? format(formData.startDateTime, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd');
                    const newDate = parse(`${dateStr} ${timeStr}`, 'yyyy-MM-dd HH:mm', new Date());
                    handleChange('startDateTime', newDate);
                  }}
                  disabled={loading || success}
                  required
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  helperText={availability === 'occupied' ? t('booking.timeSlotNotAvailable') : availability === 'available' ? t('booking.timeSlotAvailable') : ''}
                  error={availability === 'occupied'}
                />
              </Grid>
            </Grid>

            <TextField
              label={t('booking.customerName')}
              value={formData.customerName}
              onChange={(e) => handleChange('customerName', e.target.value)}
              disabled={loading || success}
              required
              fullWidth
            />

            <TextField
              label={t('booking.customerEmail')}
              type="email"
              value={formData.customerEmail}
              onChange={(e) => handleChange('customerEmail', e.target.value)}
              disabled={loading || success}
              fullWidth
            />

            <TextField
              label={t('booking.customerPhone')}
              type="tel"
              value={formData.customerPhone}
              onChange={(e) => handleChange('customerPhone', e.target.value)}
              disabled={loading || success}
              fullWidth
            />

            <TextField
              label={t('booking.notes')}
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              disabled={loading || success}
              multiline
              rows={3}
              fullWidth
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            {t('booking.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || success || !formData.meetingType || availability === 'occupied'}
          >
            {loading ? <CircularProgress size={24} /> : (isRescheduling ? t('booking.rebookMeeting') : t('booking.bookMeeting'))}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default BookMeetingDialog;
